import { useState } from 'react';
import type { DocumentData } from 'firebase/firestore';
import { Markdown } from '../Markdown';
import { Loader } from '@/components/ui/loader';
import { buttonClassName } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ChevronRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useT } from '@/lib/i18n';

export function ModuleTab({ moduleKey, module }: { moduleKey: string; module?: DocumentData }) {
  const [view, setView] = useState<'report' | 'json'>('report');
  const { isGlobalAdmin } = useAuth();
  const { t } = useT();

  if (!module) return <Loader label={t('audit.module.not_loaded')} />;

  const effectiveView = isGlobalAdmin ? view : 'report';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setView('report')}
          className={buttonClassName('ghost', effectiveView === 'report' ? 'bg-slate-100 dark:bg-secondary' : undefined)}
        >{t('audit.module.report')}</button>
        {isGlobalAdmin && (
          <button
            type="button"
            onClick={() => setView('json')}
            className={buttonClassName('ghost', effectiveView === 'json' ? 'bg-slate-100 dark:bg-secondary' : undefined)}
          >{t('audit.module.json')}</button>
        )}
        <span className="ml-auto text-xs text-ink-500">{moduleKey}</span>
      </div>

      {effectiveView === 'report' && (
        <Card>
          <Markdown source={module.reportMd} />
        </Card>
      )}

      {effectiveView === 'json' && (
        <Card>
          <JsonView data={module.json} noDataLabel={t('audit.module.no_json')} />
        </Card>
      )}
    </div>
  );
}

function JsonView({ data, noDataLabel }: { data: unknown; noDataLabel: string }) {
  if (data == null) return <div className="text-sm text-ink-500">{noDataLabel}</div>;
  return (
    <div className="font-mono text-xs">
      <Tree data={data} initialOpen depth={0} />
    </div>
  );
}

function Tree({ data, initialOpen = false, depth = 0 }: { data: unknown; initialOpen?: boolean; depth?: number }) {
  const [open, setOpen] = useState(initialOpen || depth < 2);
  if (data === null) return <span className="text-ink-400">null</span>;
  if (typeof data !== 'object') {
    return <span className={typeof data === 'string' ? 'text-emerald-600 dark:text-emerald-400' : 'text-brand-600 dark:text-brand-300'}>{JSON.stringify(data)}</span>;
  }
  const isArr = Array.isArray(data);
  const entries = Object.entries(data as Record<string, unknown>);
  if (entries.length === 0) return <span>{isArr ? '[]' : '{}'}</span>;

  return (
    <div>
      <button onClick={() => setOpen(o => !o)} className="inline-flex items-center gap-0.5 text-ink-500 hover:text-brand-500">
        <ChevronRight className={`h-3 w-3 transition ${open ? 'rotate-90' : ''}`} />
        <span>{isArr ? `[${entries.length}]` : `{${entries.length}}`}</span>
      </button>
      {open && (
        <ul className="mt-1 space-y-0.5 border-l border-slate-200 pl-3 dark:border-border">
          {entries.map(([k, v]) => (
            <li key={k}>
              <span className="text-ink-700 dark:text-foreground/80">{isArr ? '' : `"${k}": `}</span>
              <Tree data={v} depth={depth + 1} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
